/**
 * Evacuation-order draw tool (#445-(3)) — a Cesium ScreenSpaceEventHandler that lets
 * the operator draw the evacuation AREA directly on the COP, then declare an order
 * on behalf of a named issuing authority.
 *
 *   LEFT_CLICK         add a vertex
 *   MOUSE_MOVE         live "rubber-band" preview from the last vertex to cursor
 *   LEFT_DOUBLE_CLICK  close the polygon (≥ 3 distinct vertices) → onComplete(ring)
 *   RIGHT_CLICK / ESC  cancel
 *
 * A clone of SectorDrawTool (the generic polygon drawer) with a DISTINCT
 * CustomDataSource name ('operator-evac-draw') so the two draw scaffolds never
 * collide, and the VIOLET evacuation palette (evacuationStyle) for the in-progress
 * stroke so it reads as a protective-action zone. onComplete receives a CLOSED
 * GeoJSON ring ([lon,lat][], first == last) ready to wrap in a Polygon.
 */
import * as Cesium from 'cesium';
import { evacuationStyle } from './styles/evacuationStyles';

/** A simple dashed rubber-band (16-bit pattern), independent of the sector palette. */
const EVAC_DRAW_DASH = 0x0f0f;

export interface EvacuationDrawToolOptions {
  /** Fired with the closed ring when the operator double-clicks to finish. */
  onComplete: (ring: Array<[number, number]>) => void;
  /** Fired on cancel (RIGHT_CLICK / ESC / programmatic). */
  onCancel?: () => void;
  /** Fired whenever drawing starts/stops or the vertex count changes (for UI). */
  onStateChange?: (active: boolean, vertexCount: number) => void;
}

export interface EvacuationDrawToolHandle {
  start(): void;
  cancel(): void;
  isActive(): boolean;
  /**
   * #457 — render a GENERATED (non-drawn) evacuation ring as a static preview in
   * the same violet palette + fly to frame it, so the one-click zone is visible
   * (never a black box) while the operator reviews it in the declare modal.
   */
  preview(ring: Array<[number, number]>): void;
  /** Clear the generated-zone preview (on submit / cancel / redraw). */
  clearPreview(): void;
  destroy(): void;
}

/** Two lon/lat points are "the same vertex" if within ~1e-5° (≈ 1 m). */
function samePoint(a: [number, number], b: [number, number]): boolean {
  return Math.abs(a[0] - b[0]) < 1e-5 && Math.abs(a[1] - b[1]) < 1e-5;
}

export function installEvacuationDrawTool(
  viewer: Cesium.Viewer,
  opts: EvacuationDrawToolOptions,
): EvacuationDrawToolHandle {
  const ds = new Cesium.CustomDataSource('operator-evac-draw');
  void viewer.dataSources.add(ds);

  let active = false;
  let positions: Cesium.Cartesian3[] = [];
  let lonlats: Array<[number, number]> = [];
  let cursor: Cesium.Cartesian3 | null = null;
  let lastVertexAt = 0;
  let handler: Cesium.ScreenSpaceEventHandler | null = null;
  let keyListener: ((e: KeyboardEvent) => void) | null = null;

  // The ORDER violet — pairs the draw scaffold with the persisted evac palette.
  const strokeColor = Cesium.Color.fromCssColorString(evacuationStyle('order').color);

  /** Resolve a screen position to a world Cartesian3 on the globe/ellipsoid. */
  const screenToWorld = (pos: Cesium.Cartesian2): Cesium.Cartesian3 | null => {
    const ray = viewer.camera.getPickRay(pos);
    const ground =
      (ray && viewer.scene.globe.pick(ray, viewer.scene)) ||
      viewer.camera.pickEllipsoid(pos, viewer.scene.globe.ellipsoid);
    return ground ?? null;
  };

  const worldToLonLat = (c: Cesium.Cartesian3): [number, number] => {
    const carto = Cesium.Cartographic.fromCartesian(c);
    return [Cesium.Math.toDegrees(carto.longitude), Cesium.Math.toDegrees(carto.latitude)];
  };

  /** The live point list (committed vertices + the moving cursor). */
  const livePoints = (): Cesium.Cartesian3[] => (cursor ? [...positions, cursor] : positions);

  const renderScaffold = (): void => {
    ds.entities.removeAll();
    // Faint preview fill (needs ≥ 3 points to draw).
    ds.entities.add({
      polygon: {
        hierarchy: new Cesium.CallbackProperty(
          () => new Cesium.PolygonHierarchy(livePoints()),
          false,
        ),
        material: strokeColor.withAlpha(0.12),
        outline: false,
      },
    });
    // Dashed rubber-band outline (closes the loop visually once ≥ 3 points).
    ds.entities.add({
      polyline: {
        positions: new Cesium.CallbackProperty(() => {
          const pts = livePoints();
          return pts.length >= 3 ? [...pts, pts[0]!] : pts;
        }, false),
        width: 2,
        material: new Cesium.PolylineDashMaterialProperty({
          color: strokeColor.withAlpha(0.95),
          dashPattern: EVAC_DRAW_DASH,
        }),
        clampToGround: true,
      },
    });
  };

  /** Add a vertex dot entity. */
  const addVertexDot = (c: Cesium.Cartesian3): void => {
    ds.entities.add({
      position: c,
      point: {
        pixelSize: 8,
        color: strokeColor,
        outlineColor: Cesium.Color.fromCssColorString('#0b1220'),
        outlineWidth: 1,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });
  };

  const reset = (): void => {
    positions = [];
    lonlats = [];
    cursor = null;
    lastVertexAt = 0;
    ds.entities.removeAll();
  };

  const stop = (): void => {
    active = false;
    if (handler) {
      handler.destroy();
      handler = null;
    }
    if (keyListener) {
      window.removeEventListener('keydown', keyListener);
      keyListener = null;
    }
    reset();
    opts.onStateChange?.(false, 0);
  };

  const cancel = (): void => {
    if (!active) return;
    stop();
    opts.onCancel?.();
  };

  const finish = (): void => {
    if (positions.length < 3) return; // need a real polygon
    const ring: Array<[number, number]> = [...lonlats];
    // Close the ring (GeoJSON requires first == last).
    if (!samePoint(ring[0]!, ring[ring.length - 1]!)) ring.push([...ring[0]!]);
    stop();
    opts.onComplete(ring);
  };

  const start = (): void => {
    if (active) return;
    active = true;
    reset();
    renderScaffold();
    opts.onStateChange?.(true, 0);

    handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);

    handler.setInputAction((click: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      const world = screenToWorld(click.position);
      if (!world) return;
      const ll = worldToLonLat(world);
      // Skip a near-duplicate of the last vertex (the paired click of a double).
      if (lonlats.length > 0 && samePoint(ll, lonlats[lonlats.length - 1]!)) return;
      positions.push(world);
      lonlats.push(ll);
      lastVertexAt = performance.now();
      addVertexDot(world);
      opts.onStateChange?.(true, lonlats.length);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    handler.setInputAction((movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
      const world = screenToWorld(movement.endPosition);
      if (world) cursor = world;
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    handler.setInputAction(() => {
      // The double-click's paired single LEFT_CLICK just added a vertex at the
      // finish spot — pop it so the polygon doesn't gain a spurious point.
      if (positions.length > 3 && performance.now() - lastVertexAt < 500) {
        positions.pop();
        lonlats.pop();
      }
      finish();
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    handler.setInputAction(() => cancel(), Cesium.ScreenSpaceEventType.RIGHT_CLICK);

    keyListener = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') cancel();
    };
    window.addEventListener('keydown', keyListener);
  };

  /** Render a generated ring as a static preview (violet fill + dashed outline). */
  const preview = (ring: Array<[number, number]>): void => {
    if (active) stop(); // never preview mid-draw
    ds.entities.removeAll();
    if (ring.length < 3) return;
    const pts = Cesium.Cartesian3.fromDegreesArray(ring.flat());
    ds.entities.add({
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy(pts),
        material: strokeColor.withAlpha(0.15),
        outline: false,
      },
    });
    ds.entities.add({
      polyline: {
        positions: [...pts, pts[0]!],
        width: 2,
        material: new Cesium.PolylineDashMaterialProperty({
          color: strokeColor.withAlpha(0.95),
          dashPattern: EVAC_DRAW_DASH,
        }),
        clampToGround: true,
      },
    });
    // Frame the generated zone so the operator can see exactly what they'll issue.
    void viewer.flyTo(ds, { duration: 0.8 });
  };

  const clearPreview = (): void => {
    if (active) return; // don't wipe an in-progress draw
    ds.entities.removeAll();
  };

  return {
    start,
    cancel,
    isActive(): boolean {
      return active;
    },
    preview,
    clearPreview,
    destroy(): void {
      stop();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
